import { SITE_TODAY, matchesAudience } from "@/lib/catalog";
import { getIndexedWork } from "@/lib/catalogIndex";
import { normalizeText } from "@/lib/normalize";
import { matchesPriceBand } from "@/lib/pricing";
import {
  isReleased,
  isUpcoming,
  releaseDayDiff,
  withinReleasedDays,
} from "@/lib/releaseDate";
import { resolveGenreIds } from "@/lib/taxonomy";
import type { AudienceType, Work, WorkFilters } from "@/types/work";

/**
 * ファセット集計。
 *
 * 「各ファセットは自分自身の絞り込みを外した状態で数える」必要があるため、
 * 完全な単一走査にはできない。ただし作品ごとの条件判定を1回だけ行い、
 * その結果を各ファセットで使い回すことで、全件走査の回数を抑えている。
 */

export interface FacetCounts {
  total: number;
  genres: Record<string, number>;
  types: Record<string, number>;
  makers: Record<string, number>;
  audiences: Record<string, number>;
  people: Record<string, number>;
}

type DimensionKey =
  | "audience"
  | "types"
  | "genres"
  | "person"
  | "makers"
  | "release"
  | "price"
  | "rating"
  | "video"
  | "sale"
  | "series"
  | "attributes";

type DimensionFlags = Record<DimensionKey, boolean>;

const DIMENSION_KEYS: DimensionKey[] = [
  "audience",
  "types",
  "genres",
  "person",
  "makers",
  "release",
  "price",
  "rating",
  "video",
  "sale",
  "series",
  "attributes",
];

/**
 * 価格帯の判定は lib/pricing.ts に一本化している（ラベルと判定を同じ定義から生成するため）。
 * 既存のインポート元を壊さないように再エクスポートする。
 */
export { matchesPriceBand };

/** 属性値（"118" / "118分" など）を数値として読む */
function readAttributeNumber(work: Work, attributeId: string): number | null {
  const entry = work.attributes.find((attribute) => attribute.attributeId === attributeId);
  if (!entry) return null;
  const parsed = Number.parseFloat(entry.value.replace(/[^0-9.]/g, ""));
  return Number.isFinite(parsed) ? parsed : null;
}

/** 属性レンジ（収録時間・ページ数など）の条件。値が無い作品は該当しない。 */
function matchesAttributes(work: Work, filters: WorkFilters): boolean {
  if (filters.attributes.length === 0) return true;
  return filters.attributes.every((filter) => {
    const value = readAttributeNumber(work, filter.attributeId);
    if (value === null) return false;
    if (filter.min !== null && value < filter.min) return false;
    if (filter.max !== null && value > filter.max) return false;
    return true;
  });
}

/** 直近N日（発売済みのみ・日本時間基準） */
export function withinDays(dateString: string, days: number, reference: Date = SITE_TODAY): boolean {
  return withinReleasedDays(dateString, days, reference);
}

export { isReleased, isUpcoming, releaseDayDiff };

function matchesReleaseStatus(work: Work, filters: WorkFilters, reference: Date): boolean {
  const statusOk =
    filters.releaseStatus === "any"
      ? true
      : filters.releaseStatus === "released"
        ? isReleased(work.releaseDate, reference)
        : isUpcoming(work.releaseDate, reference);
  if (!statusOk) return false;
  // 直近N日は「発売済み」の条件なので、発売予定指定とは同時に成立しない
  if (filters.days !== null) return withinReleasedDays(work.releaseDate, filters.days, reference);
  return true;
}

function evaluateFlags(work: Work, filters: WorkFilters, mode: AudienceType, genreIds: string[]): DimensionFlags {
  const personToken = normalizeText(filters.person);
  const personHit =
    !personToken ||
    normalizeText(work.creator).includes(personToken) ||
    work.performers.some((performer) => normalizeText(performer).includes(personToken));
  const genreHit =
    genreIds.length === 0 ||
    (filters.genreMatch === "and"
      ? genreIds.every((id) => work.genreIds.includes(id))
      : genreIds.some((id) => work.genreIds.includes(id)));

  return {
    audience:
      filters.audience === "all" ? matchesAudience(work, mode) : work.audienceIds.includes(filters.audience),
    types: filters.types.length === 0 || filters.types.includes(work.contentType),
    genres: genreHit,
    person: personHit,
    makers: filters.makers.length === 0 || filters.makers.includes(work.maker),
    release: matchesReleaseStatus(work, filters, SITE_TODAY),
    price: filters.priceBands.length === 0 || filters.priceBands.some((band) => matchesPriceBand(work.price, band)),
    rating: filters.minRating === null || (work.rating !== null && work.rating >= filters.minRating),
    video: filters.video === null ? true : filters.video === "yes" ? Boolean(work.sampleVideoUrl) : !work.sampleVideoUrl,
    sale: filters.sale === null ? true : filters.sale === "yes" ? work.onSale : !work.onSale,
    series: filters.series === null ? true : filters.series === "series" ? Boolean(work.series) : !work.series,
    attributes: matchesAttributes(work, filters),
  };
}

function passes(flags: DimensionFlags, exclude?: DimensionKey): boolean {
  for (const key of DIMENSION_KEYS) {
    if (key === exclude) continue;
    if (!flags[key]) return false;
  }
  return true;
}

/** 現在の条件に応じたファセット件数を集計する */
export function computeFacets(pool: Work[], filters: WorkFilters, mode: AudienceType): FacetCounts {
  const genreIds = resolveGenreIds(filters.genres);
  const genres: Record<string, number> = {};
  const types: Record<string, number> = {};
  const makers: Record<string, number> = {};
  const audiences: Record<string, number> = {};
  const people: Record<string, number> = {};
  let total = 0;

  for (const work of pool) {
    const indexed = getIndexedWork(work.id);
    const flags = evaluateFlags(work, filters, mode, genreIds);

    if (passes(flags)) total += 1;

    if (passes(flags, "genres")) {
      const genreSet = indexed?.genreIdSet ?? new Set(work.genreIds);
      genreSet.forEach((id) => {
        genres[id] = (genres[id] ?? 0) + 1;
      });
    }

    if (passes(flags, "types")) {
      types[work.contentType] = (types[work.contentType] ?? 0) + 1;
    }

    if (passes(flags, "makers")) {
      makers[work.maker] = (makers[work.maker] ?? 0) + 1;
    }

    if (passes(flags, "person")) {
      people[work.creator] = (people[work.creator] ?? 0) + 1;
      work.performers.forEach((performer) => {
        people[performer] = (people[performer] ?? 0) + 1;
      });
    }

    if (passes(flags, "audience")) {
      const audienceSet = indexed?.audienceIdSet ?? new Set(work.audienceIds);
      audienceSet.forEach((id) => {
        audiences[id] = (audiences[id] ?? 0) + 1;
      });
    }
  }

  return { total, genres, types, makers, audiences, people };
}